import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import StatusPill from "@/components/StatusPill";
import { Check, AlertTriangle, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_e5fbfad1-91cd-48c5-b4b1-e04fdc4a0794/artifacts/hl0vo1yr_PROOF%20logo%20and%20concept%20round.png";

export default function Receipt() {
    const { id } = useParams();
    const [receipt, setReceipt] = useState(null);
    const [err, setErr] = useState(null);

    // noindex: unlisted receipts must not be indexed by search engines
    useEffect(() => {
        const m = document.createElement("meta");
        m.name = "robots";
        m.content = "noindex,nofollow";
        document.head.appendChild(m);
        const prevTitle = document.title;
        document.title = "Receipt · ProofCommand";
        return () => {
            document.head.removeChild(m);
            document.title = prevTitle;
        };
    }, []);

    useEffect(() => {
        axios
            .get(`${BACKEND_URL}/api/receipts/${id}`)
            .then((r) => setReceipt(r.data))
            .catch((e) => setErr(e?.response?.status === 404 ? "not_found" : "error"));
    }, [id]);

    if (err === "not_found") return <Framed><NotFound /></Framed>;
    if (err) return <Framed><div className="font-mono text-[#ff453a]">load error</div></Framed>;
    if (!receipt) return <Framed><div className="font-mono text-[#9ca3af]">loading…</div></Framed>;

    const statusMeta = {
        success: { Icon: Check, label: "Verified delivered", accent: "#7cc93c" },
        failed: { Icon: AlertTriangle, label: "Delivery failed", accent: "#ff453a" },
        pending: { Icon: Clock, label: "Pending", accent: "#ff9f0a" },
        needs_review: { Icon: Clock, label: "Pending review", accent: "#ff9f0a" },
        resolved: { Icon: Check, label: "Resolved by operator", accent: "#9ca3af" },
        dismissed: { Icon: AlertTriangle, label: "Dismissed", accent: "#9ca3af" },
        running: { Icon: Clock, label: "Running", accent: "#0a84ff" },
    }[receipt.status] || { Icon: Clock, label: receipt.status, accent: "#9ca3af" };

    return (
        <Framed>
            <div className="max-w-xl w-full pc-fade-up" data-testid="receipt-page">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <img src={LOGO_URL} alt="ProofCommand" className="w-10 h-10" />
                    <div>
                        <div className="font-sans font-bold tracking-tight">ProofCommand</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#9ca3af]">
                            public receipt · no claims, receipts.
                        </div>
                    </div>
                </div>

                {/* Hero */}
                <div
                    className="border border-[#262a2e] bg-[#111315] p-8 mb-6"
                    style={{ borderTop: `2px solid ${statusMeta.accent}` }}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <statusMeta.Icon className="w-5 h-5" style={{ color: statusMeta.accent }} />
                        <div
                            className="font-sans text-xl font-semibold tracking-tight"
                            style={{ color: statusMeta.accent }}
                        >
                            {statusMeta.label}
                        </div>
                        <span className="ml-auto">
                            <StatusPill status={receipt.status} />
                        </span>
                    </div>

                    <KV label="Type" value={receipt.type} mono />
                    <KV label="Target" value={receipt.target || "—"} mono />
                    <KV label="Provider" value={receipt.provider || "—"} mono />
                    <KV
                        label="Subject"
                        value={receipt.payload?.subject || "—"}
                    />
                    <KV label="Workflow" value={receipt.workflow || "—"} mono />
                    <KV label="Created" value={fmt(receipt.created_at)} mono />
                    <KV label="Updated" value={fmt(receipt.updated_at)} mono />
                    <KV label="Retries" value={String(receipt.retry_count ?? 0)} mono />
                    <KV label="Attempts" value={String(receipt.attempts_count ?? 0)} mono />
                    {receipt.response?.message_id && (
                        <KV label="Provider ID" value={receipt.response.message_id} mono />
                    )}
                </div>

                {/* Preview */}
                {receipt.payload?.preview && (
                    <div className="border border-[#262a2e] bg-[#111315] p-6 mb-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af] mb-2">
                            message preview
                        </div>
                        <div className="font-mono text-[12px] whitespace-pre-wrap text-[#f3f4f6] leading-relaxed">
                            {receipt.payload.preview}
                            {receipt.payload.preview.length >= 240 && "…"}
                        </div>
                    </div>
                )}

                {receipt.error && (
                    <div className="border border-[#ff453a]/30 bg-[#ff453a]/5 p-6 mb-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#ff453a] mb-2">
                            error
                        </div>
                        <div className="font-mono text-[12px] text-[#ff453a]">{receipt.error}</div>
                    </div>
                )}

                {/* ID + footer */}
                <div className="border-t border-[#262a2e] pt-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af] mb-1">
                        receipt id
                    </div>
                    <div
                        className="font-mono text-[11px] text-[#9ca3af] break-all"
                        data-testid="receipt-id"
                    >
                        {receipt.id}
                    </div>
                </div>

                <div className="mt-10 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                    <span>verified via proofcommand</span>
                    <Link to="/dashboard" className="hover:text-[#7cc93c]">
                        open dashboard →
                    </Link>
                </div>
            </div>
        </Framed>
    );
}

function Framed({ children }) {
    return (
        <div className="min-h-screen pc-grid-bg bg-[#090a0b] text-[#f3f4f6] flex items-start justify-center p-8 sm:p-16">
            {children}
        </div>
    );
}

function NotFound() {
    return (
        <div className="max-w-md pc-fade-up" data-testid="receipt-not-found">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff453a] mb-2">
                404
            </div>
            <div className="text-3xl font-bold tracking-tight mb-2">Receipt not found</div>
            <div className="font-mono text-[11px] text-[#9ca3af]">
                This receipt either doesn't exist or has been removed.
            </div>
        </div>
    );
}

function KV({ label, value, mono }) {
    return (
        <div className="grid grid-cols-[140px_1fr] gap-4 py-2 border-b last:border-b-0 border-[#262a2e]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                {label}
            </div>
            <div
                className={`text-[13px] ${mono ? "font-mono" : "font-sans"} text-[#f3f4f6] break-all`}
            >
                {value}
            </div>
        </div>
    );
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + "Z";
    } catch {
        return iso;
    }
}
